'use client';
import { usePathname } from 'next/navigation';

import { CMSLink } from '@/components/Link';
import { useFrontPageEffect } from '@/contexts/FrontPageEffectContext';
import type { Header } from '@/payload-types';
import { cn } from '@/utils/cn';

import { MobileNavigation } from './MobileNavigation';
import styles from './Navigation.module.scss';

export function Navigation({
  locale,
  mobileNavOpen = false,
  mobileNavigation,
  navigation,
  setMobileNavOpen = () => {},
}: {
  navigation: Header['navigation'];
  mobileNavigation: Header['mobileNavigation'];
  mobileNavOpen?: boolean;
  setMobileNavOpen?: (open: boolean) => void;
  locale: string;
}) {
  const pathname = usePathname();
  const { videoLoaded } = useFrontPageEffect();

  const isFrontPage = pathname === '/';

  const handleClick = () => {
    setMobileNavOpen(!mobileNavOpen);

    // Prevent scrolling when the navigation is open
    if (!mobileNavOpen) {
      document.body.style.overflow = 'hidden';
      // Make sure its also locked on mobile
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      // Make sure its also unlocked on mobile
      document.documentElement.style.overflow = '';
    }
  };

  if (navigation?.length === 0) return null;

  return (
    <>
      <svg
        className={cn(
          styles.ham,
          mobileNavOpen && styles.active,
          isFrontPage && styles.isFrontPage,
          videoLoaded && isFrontPage && styles.videoLoaded
        )}
        viewBox='0 0 100 100'
        onClick={handleClick}
      >
        <path
          className={cn(styles['line'], styles['top'])}
          d='m 30,33 h 40 c 3.722839,0 7.5,3.126468 7.5,8.578427 0,5.451959 -2.727029,8.421573 -7.5,8.421573 h -20'
        />
        <path
          className={cn(styles['line'], styles['middle'])}
          d='m 30,50 h 40'
        />
        <path
          className={cn(styles['line'], styles['bottom'])}
          d='m 70,67 h -40 c 0,0 -7.5,-0.802118 -7.5,-8.365747 0,-7.563629 7.5,-8.634253 7.5,-8.634253 h 20'
        />
      </svg>

      <nav
        className={cn(
          styles.navigation,
          isFrontPage && styles.isFrontPage,
          videoLoaded && isFrontPage && styles.videoLoaded
        )}
      >
        {navigation?.map((item, index) => {
          const href =
            (item.link.type === 'reference' &&
            typeof item.link.reference?.value === 'object'
              ? typeof item.link.reference?.value?.url === 'object'
                ? item.link.reference?.value?.url?.[locale]
                : item.link.reference?.value?.url
              : typeof item.link.reference?.value === 'object'
                ? item.link.reference?.value?.url
                : undefined) || item.link.url;

          if (!href) return null;

          return (
            <CMSLink
              key={index}
              locale={locale}
              {...item.link}
              className={cn(
                pathname.includes(`${href}/`) ? styles['activeLink'] : ''
              )}
            />
          );
        })}
      </nav>
      <MobileNavigation
        open={mobileNavOpen}
        locale={locale}
        mobileNavigation={mobileNavigation}
      />
    </>
  );
}
